import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";

// Same health logic used on Client Portfolio and the CEO Dashboard, duplicated
// here on purpose since it's a small pure function.
function computeHealth(attainmentPct, hasOverdueInvoice) {
  if (hasOverdueInvoice) return attainmentPct !== null && attainmentPct < 60 ? "red" : "amber";
  if (attainmentPct === null) return "green";
  if (attainmentPct >= 90) return "green";
  if (attainmentPct >= 60) return "amber";
  return "red";
}

export default function AgencyOverview() {
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [metricsThisMonth, setMetricsThisMonth] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    const thisMonth = new Date().toISOString().slice(0, 7);
    const monthStart = `${thisMonth}-01`;

    const [clientsRes, projectsRes, invoicesRes, metricsRes, activityRes] = await Promise.all([
      supabase.from("clients").select("*").order("created_at", { ascending: false }),
      supabase.from("projects").select("*, project_types(name)"),
      supabase.from("invoices").select("*"),
      supabase.from("metrics").select("*, projects!inner(client_id)").gte("date", monthStart),
      supabase
        .from("activity_log")
        .select("*, users(full_name, email), clients(company_name)")
        .order("created_at", { ascending: false })
        .limit(5),
    ]);

    setClients(clientsRes.data || []);
    setProjects(projectsRes.data || []);
    setInvoices(invoicesRes.data || []);
    setMetricsThisMonth(metricsRes.data || []);
    setActivity(activityRes.data || []);
    setLoading(false);
  }

  const clientRows = useMemo(() => {
    return clients.map((cl) => {
      const clientProjects = projects.filter((p) => p.client_id === cl.id);
      const clientInvoices = invoices.filter((i) => i.client_id === cl.id);
      const hasOverdue = clientInvoices.some((i) => i.status === "overdue");

      const goalPcts = [];
      clientProjects.forEach((p) => {
        Object.entries(p.goal_targets || {}).forEach(([key, target]) => {
          const actual = metricsThisMonth
            .filter((m) => m.project_id === p.id && m.metric_key === key)
            .reduce((sum, m) => sum + Number(m.value), 0);
          if (Number(target) > 0) goalPcts.push((actual / Number(target)) * 100);
        });
      });
      const attainment = goalPcts.length > 0 ? Math.round(goalPcts.reduce((a, b) => a + b, 0) / goalPcts.length) : null;
      const health = computeHealth(attainment, hasOverdue);

      return {
        ...cl,
        objective: clientProjects[0]?.project_types?.name || "Custom",
        attainment,
        hasOverdue,
        health,
      };
    });
  }, [clients, projects, invoices, metricsThisMonth]);

  const kpis = useMemo(() => {
    const activeClients = clients.filter((c) => c.lifecycle_status === "active").length;
    const totalSpend = metricsThisMonth.filter((m) => m.metric_key === "spend").reduce((sum, m) => sum + Number(m.value), 0);
    const overdue = invoices.filter((i) => i.status === "overdue");
    const overdueAmount = overdue.reduce((sum, i) => sum + Number(i.amount), 0);
    const totalLeads = metricsThisMonth.filter((m) => m.metric_key === "leads").reduce((sum, m) => sum + Number(m.value), 0);
    const totalOrders = metricsThisMonth.filter((m) => m.metric_key === "orders").reduce((sum, m) => sum + Number(m.value), 0);
    const flagged = clientRows.filter((c) => c.health === "red").length;

    return { activeClients, totalSpend, overdueCount: overdue.length, overdueAmount, totalLeads, totalOrders, flagged };
  }, [clients, metricsThisMonth, invoices, clientRows]);

  const topPerforming = [...clientRows]
    .filter((c) => c.attainment !== null)
    .sort((a, b) => b.attainment - a.attainment)
    .slice(0, 3);

  const needsAttention = clientRows.filter((c) => c.health !== "green").slice(0, 3);

  if (loading) return <p className="text-sm text-muted-foreground">Loading...</p>;

  return (
    <div>
      <h1 className="text-xl font-semibold text-foreground mb-1">Overview</h1>
      <p className="text-sm text-muted-foreground mb-5">{clients.length} clients</p>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Kpi label="Active clients" value={kpis.activeClients} />
        <Kpi label="Total spend, all clients" value={`₹${kpis.totalSpend.toLocaleString("en-IN")}`} sub="this month" />
        <Kpi label="Overdue invoices" value={kpis.overdueCount} sub={kpis.overdueCount > 0 ? `₹${kpis.overdueAmount.toLocaleString("en-IN")} outstanding` : null} />
        <Kpi label="Total leads generated" value={kpis.totalLeads.toLocaleString("en-IN")} sub="this month, lead gen projects" />
        <Kpi label="Total orders generated" value={kpis.totalOrders.toLocaleString("en-IN")} sub="this month, sales projects" />
        <Kpi label="Flagged projects" value={kpis.flagged} sub="needs attention" />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">
            Top performing clients
          </div>
          {topPerforming.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5">Not enough goal data yet.</p>
          ) : (
            topPerforming.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-5 py-3 border-b border-border last:border-0">
                <div>
                  <div className="text-sm font-medium">{c.company_name}</div>
                  <div className="text-xs text-muted-foreground">{c.objective}</div>
                </div>
                <span className="font-mono text-xs bg-accent text-foreground rounded-full px-2.5 py-1">
                  {c.attainment}% of goal
                </span>
              </div>
            ))
          )}
        </div>

        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">
            Needs attention
          </div>
          {needsAttention.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5">Nothing flagged right now.</p>
          ) : (
            needsAttention.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-5 py-3 border-b border-border last:border-0">
                <div>
                  <div className="text-sm font-medium">{c.company_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {c.hasOverdue ? "Invoice overdue" : c.objective}
                  </div>
                </div>
                <span className="font-mono text-xs bg-destructive/10 text-destructive rounded-full px-2.5 py-1">
                  {c.attainment !== null ? `${c.attainment}% of goal` : "Overdue"}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="grid grid-cols-[1.4fr_1fr] gap-4">
        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">Clients</div>
          <table className="w-full text-sm">
            <tbody>
              {clientRows.map((c) => (
                <tr key={c.id} className="border-b border-border last:border-0 hover:bg-accent/60">
                  <td className="px-5 py-3">
                    <Link to={`/dashboard/agency/clients/${c.id}`} className="font-medium text-foreground hover:underline">
                      {c.company_name}
                    </Link>
                    <div className="text-xs text-muted-foreground">{c.objective}</div>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <HealthDot health={c.health} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="border border-border rounded-2xl bg-card overflow-hidden">
          <div className="px-5 py-3.5 border-b border-border font-semibold text-sm text-foreground">
            Recent activity
          </div>
          {activity.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5">Nothing logged yet.</p>
          ) : (
            activity.map((a) => (
              <div key={a.id} className="px-5 py-3 border-b border-border last:border-0">
                <div className="text-xs text-foreground">
                  <strong>{a.users?.full_name || a.users?.email}</strong> {a.action.toLowerCase()}
                  {a.clients?.company_name && <> for <strong>{a.clients.company_name}</strong></>}
                </div>
                <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                  {new Date(a.created_at).toLocaleString()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub }) {
  return (
    <div className="border border-border rounded-2xl bg-card p-4">
      <div className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">{label}</div>
      <div className="text-lg font-semibold text-foreground">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function HealthDot({ health }) {
  const colors = { green: "bg-primary", amber: "bg-yellow-600", red: "bg-destructive" };
  const labels = { green: "Good", amber: "Watch", red: "At risk" };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs justify-end">
      <span className={`w-2.5 h-2.5 rounded-full ${colors[health]}`} />
      {labels[health]}
    </span>
  );
}
